import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatApiError } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, ShieldCheck, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import logo from "@/assets/pacazur-logo.png";
import LegalFooter from "@/components/LegalFooter";

import LOGIN_BG from "@/assets/login-security-bg.jpg";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [remember, setRemember] = useState(true);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, remember });
      if (data.token) {
        localStorage.setItem("auth_token", data.token);
      }
      localStorage.removeItem("agent_mode");
      setUser(data);
      toast.success(`Bienvenue, ${data.name}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
    } catch {
      // do not reveal whether the email exists
    } finally {
      setForgotSent(true);
      setForgotLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#05070A] overflow-hidden p-4 sm:p-8" data-testid="login-page">
      <img src={LOGIN_BG} alt="" data-testid="login-bg-image" className="absolute inset-0 w-full h-full object-cover object-left opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#05070A]/70 via-[#05070A]/75 to-[#05070A]/90" />
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(0,122,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(0,122,255,.6) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
      <div className="absolute top-5 left-5 sm:top-8 sm:left-8 z-10 hidden sm:flex items-center gap-2 text-[11px] font-code uppercase tracking-[0.2em] text-zinc-400">
        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Système sécurisé · Connexion chiffrée
      </div>
      <div className="relative z-10 w-full max-w-md fade-up">
        <div className="rounded-2xl border border-white/10 bg-[#0A0D12]/60 backdrop-blur-xl shadow-[0_20px_80px_rgba(0,0,0,0.6)] p-6 sm:p-9" data-testid="login-card">
          <div className="h-0.5 -mt-6 sm:-mt-9 mb-6 sm:mb-8 -mx-6 sm:-mx-9 rounded-t-2xl bg-gradient-to-r from-transparent via-[#007AFF] to-transparent" />
          <div className="flex items-center gap-3 mb-8">
            <img src={logo} alt="PACAZUR" className="w-11 h-11 object-contain" />
            <div>
              <span className="font-display font-semibold text-lg tracking-tight block">PACAZUR-KEYS</span>
              <span className="text-[11px] text-zinc-400 font-code flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#FFB020]" /> Sécurité & gestion des accès</span>
            </div>
          </div>

          <h2 className="font-display text-2xl font-medium tracking-tight">Connexion</h2>
          <p className="text-zinc-400 text-sm mt-2 mb-8">Accédez à votre espace de gestion.</p>

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input
                id="email" type="email" required data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@societe.fr"
                className="bg-black/40 border-zinc-700/70 focus:border-[#007AFF] focus-visible:ring-1 focus-visible:ring-[#007AFF] h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Mot de passe</Label>
              <PasswordInput
                id="password" required data-testid="login-password-input"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-black/40 border-zinc-700/70 focus:border-[#007AFF] focus-visible:ring-1 focus-visible:ring-[#007AFF] h-11"
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer select-none">
                <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="remember-me-checkbox" className="border-zinc-700 data-[state=checked]:bg-[#007AFF] data-[state=checked]:border-[#007AFF]" />
                Se souvenir de moi (30 jours)
              </label>
              <button type="button" onClick={() => { setForgotEmail(email); setForgotSent(false); setForgotOpen(true); }} data-testid="forgot-password-link" className="text-sm text-[#007AFF] hover:underline">
                Mot de passe oublié ?
              </button>
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <Button
              type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-11 bg-[#007AFF] hover:bg-[#3395FF] text-white font-medium transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
            </Button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-zinc-500 uppercase tracking-widest">ou</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <Button
            onClick={handleGoogle} variant="outline" data-testid="login-google-button"
            className="w-full h-11 bg-black/30 border-zinc-700/70 hover:bg-white/5 text-white transition-colors"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
            Continuer avec Google
          </Button>

          <p className="text-xs text-zinc-500 mt-6 text-center">
            La connexion Google crée automatiquement un compte agent.
          </p>
        </div>
        <a href="/guide" data-testid="login-guide-link" className="mt-6 block text-center text-sm text-[#4da3ff] hover:underline">Guide d'utilisation (agents)</a>
        <LegalFooter className="mt-3" />
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display text-xl">Mot de passe oublié</DialogTitle></DialogHeader>
          {forgotSent ? (
            <div className="text-center py-4" data-testid="forgot-sent">
              <MailCheck className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <p className="text-zinc-300 text-sm">Si un compte existe avec cet email, un lien de réinitialisation vient d'être envoyé. Vérifiez votre boîte mail (et les spams).</p>
              <Button onClick={() => setForgotOpen(false)} className="mt-5 bg-[#007AFF] hover:bg-[#3395FF] text-white">Fermer</Button>
            </div>
          ) : (
            <form onSubmit={handleForgot} className="space-y-4 mt-2">
              <p className="text-zinc-400 text-sm">Entrez votre email : nous vous enverrons un lien pour choisir un nouveau mot de passe.</p>
              <div className="space-y-2">
                <Label className="text-zinc-300">Email</Label>
                <Input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} data-testid="forgot-email-input" placeholder="vous@societe.fr" className="bg-zinc-900 border-zinc-800 focus:border-[#007AFF] focus-visible:ring-1 focus-visible:ring-[#007AFF] h-11" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={forgotLoading} data-testid="forgot-submit-button" className="bg-[#007AFF] hover:bg-[#3395FF] text-white">
                  {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer le lien"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
